import React from 'react';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import SubscriptionDialog, { DialogButton, DialogNotice } from '../../../components/subscription/SubscriptionDialog';
import usePlanChangeSimulation from './usePlanChangeSimulation';
import { buildUpgradeBenefits } from './planChangeLists';
import {
  PlanChangeComparison,
  PlanChangeList,
  PlanChangeValues,
  formatCents,
  formatPrice,
  formatShortDate,
} from './PlanChangeParts';
import styles from './PlanChangeModal.module.css';

// Mantido aqui para quem já importava daqui
export { buildUpgradeBenefits };

/**
 * Upgrade de quem já assina: de -> para, o que passa a ter e quanto é cobrado
 * hoje (diferença proporcional, pela prévia do Stripe) e na próxima cobrança.
 * Erro (ex.: cartão recusado) aparece dentro do popup; o plano só muda quando
 * Plans.js confirma.
 */
const UpgradePlanModal = ({
  open,
  currentPlan,
  newPlan,
  loading = false,
  error = null,
  success = false,
  onConfirm,
  onCancel,
}) => {
  const { simulation, loading: simulationLoading } = usePlanChangeSimulation(newPlan?.id, !!open);

  if (!open || !newPlan) return null;

  if (success) {
    return (
      <SubscriptionDialog
        open
        locked
        hideClose
        centered
        icon={<span className={styles.successIcon}><CheckRoundedIcon fontSize="inherit" /></span>}
        title="Plano atualizado"
        subtitle={<>Você agora está no plano <strong>{newPlan.name}</strong>. Os novos recursos já estão liberados.</>}
      />
    );
  }

  // Lista do que passa a ter: config > features do plano > vazio
  const configBenefits = buildUpgradeBenefits(currentPlan?.config || null, newPlan?.config || null);
  const benefits = configBenefits.length > 0
    ? configBenefits
    : (newPlan.features || [])
      .map((f) => (typeof f === 'object' ? (f.name || f.description || '') : f))
      .filter(Boolean);

  const chargeNowCents = simulation?.charge_now_cents;
  const hasChargeNow = typeof chargeNowCents === 'number' && chargeNowCents > 0;
  const nextDate = formatShortDate(simulation?.next_due_date);

  const rows = [
    {
      label: 'Cobrado hoje',
      value: hasChargeNow ? formatCents(chargeNowCents) : 'Proporcional',
      hint: 'Diferença até o fim do ciclo atual',
    },
    {
      label: 'Próxima cobrança',
      value: formatPrice(newPlan.price),
      hint: nextDate ? `Em ${nextDate}, valor cheio do novo plano` : 'Valor cheio do novo plano',
    },
  ];

  return (
    <SubscriptionDialog
      open
      onClose={onCancel}
      locked={loading}
      title={<>Ativar plano <span className={styles.titleHighlight}>{newPlan.name}</span></>}
      subtitle="Mais recursos e limites maiores, liberados assim que o pagamento for confirmado."
      secondaryAction={(
        <DialogButton variant="text" onClick={onCancel} disabled={loading}>
          Cancelar
        </DialogButton>
      )}
      actions={(
        <DialogButton onClick={() => onConfirm(newPlan)} loading={loading} loadingLabel="Confirmando...">
          {`Ativar plano ${newPlan.name}`}
        </DialogButton>
      )}
    >
      <PlanChangeComparison currentPlan={currentPlan} newPlan={newPlan} />
      <PlanChangeList title="O que você passa a ter" items={benefits} />
      <PlanChangeValues
        rows={rows}
        loading={simulationLoading}
        note="Cobrado no cartão da assinatura, pelo Stripe."
      />
      {error && (
        <DialogNotice tone="error" icon={<ErrorOutlineIcon fontSize="inherit" />}>
          {error}
        </DialogNotice>
      )}
    </SubscriptionDialog>
  );
};

export default UpgradePlanModal;
